import React, { useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { savePDFForm } from './supabaseService';

const LOGO_URL = '/logo.jpg';

const GENERAL_AREAS = [
  'Ceilings feather dusted for cobwebs',
  'All windows and glass doors cleaned',
  'Fire extinguishers wiped down',
  'Skirting boards (where accessible) cleaned',
  'Radiator fronts and dials wiped',
  'All general waste bins emptied',
];

const RECEPTION_AREA = [
  'Reception counter cleaned',
  '11 waiting area seats wiped',
  'Floor vacuumed and mopped',
];

const CLINICAL_ROOMS = [
  'All surfaces sanitized thoroughly',
  'General waste removed',
];

const OFFICES = [
  'Desks wiped',
  'Chairs wiped',
  'Telephones cleaned',
  'Keypads and screens wiped',
];

const KITCHENETTE = [
  'Cupboard fronts cleaned',
  'Surfaces cleaned',
  'Dishes put away',
  'General waste bins emptied',
];

// Define section labels and field labels for PDF generation
const sectionLabels = {
  general: 'General Areas',
  reception: 'Reception Area',
  clinical: 'Clinical Rooms',
  offices: 'Offices',
  kitchenette: 'Kitchenette'
};

const fieldLabels = {
  general: GENERAL_AREAS,
  reception: RECEPTION_AREA,
  clinical: CLINICAL_ROOMS,
  offices: OFFICES,
  kitchenette: KITCHENETTE
};

const styles = {
  container: {
    maxWidth: 700,
    margin: '40px auto',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    padding: 24,
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  title: {
    textAlign: 'center',
    fontSize: 28,
    fontWeight: 800,
    color: '#0a3d91',
    marginBottom: 8,
    letterSpacing: 1,
  },
  subtitle: {
    textAlign: 'center',
    fontSize: 16,
    color: '#222',
    marginBottom: 18,
    fontWeight: 500,
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #eee',
    fontSize: 16,
  },
  label: {
    fontWeight: 600,
    fontSize: 15,
    color: '#0a3d91',
    marginBottom: 2,
  },
  radioGroup: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 15,
    border: '1.5px solid #0a3d91',
    borderRadius: 8,
    background: '#fff',
    color: '#222',
    marginBottom: 0,
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    minHeight: 60,
    padding: '10px 12px',
    fontSize: 15,
    border: '1.5px solid #0a3d91',
    borderRadius: 8,
    background: '#fff',
    color: '#222',
    resize: 'vertical',
    marginBottom: 0,
  },
  button: {
    display: 'block',
    margin: '32px auto 0',
    padding: '12px 36px',
    fontSize: 18,
    fontWeight: 600,
    color: '#fff',
    background: 'linear-gradient(90deg, #0a3d91 0%, #222 100%)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(10,61,145,0.08)',
    transition: 'background 0.2s',
  },
  logo: {
    width: 90,
    height: 'auto',
    display: 'block',
    margin: '24px auto 0',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
};

export default function SurgeryChecklist() {
  const [date, setDate] = useState('');
  const [auditor, setAuditor] = useState('');
  const [cleaners, setCleaners] = useState('');
  const [form, setForm] = useState({
    general: GENERAL_AREAS.map(() => ''),
    reception: RECEPTION_AREA.map(() => ''),
    clinical: CLINICAL_ROOMS.map(() => ''),
    offices: OFFICES.map(() => ''),
    kitchenette: KITCHENETTE.map(() => '')
  });
  const [comments, setComments] = useState({
    general: '',
    reception: '',
    clinical: '',
    offices: '',
    kitchenette: ''
  });
  const pdfRef = useRef();

  const handleGeneralChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      general: prev.general.map((item, i) => i === idx ? value : item)
    }));
  };
  const handleReceptionChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      reception: prev.reception.map((item, i) => i === idx ? value : item)
    }));
  };
  const handleClinicalChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      clinical: prev.clinical.map((item, i) => i === idx ? value : item)
    }));
  };
  const handleOfficesChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      offices: prev.offices.map((item, i) => i === idx ? value : item)
    }));
  };
  const handleKitchenetteChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      kitchenette: prev.kitchenette.map((item, i) => i === idx ? value : item)
    }));
  };

  const handleCommentChange = (section, value) => {
    setComments(prev => ({
      ...prev,
      [section]: value
    }));
  };

  const generatePDF = async () => {
    try {
      const pdfHtml = `
        <div style="font-family: 'Segoe UI', Arial, sans-serif; background: #fff; padding: 0; margin: 0;">
          <div style="background: linear-gradient(90deg, #b00 0%, #222 100%); padding: 18px 0 12px 0; text-align: center; border-radius: 0 0 18px 18px; box-shadow: 0 2px 12px rgba(176,0,0,0.10);">
            <div style='font-size: 30px; font-weight: 900; color: #fff; letter-spacing: 1.2px; margin-bottom: 2px;'>Surgery Audit Form</div>
            <div style='font-size: 18px; color: #fff; font-weight: 500; margin-top: 8px;'>Date: ${date || '<span style="color:#eee">(No date)</span>'} &nbsp; | &nbsp; Staff: ${auditor || '<span style="color:#eee">(No name)</span>'}</div>
          </div>
          <div style="padding: 24px;">
            ${Object.keys(sectionLabels).map((section, idx) => `
              <div style='margin-bottom: 32px; background: #fafbfc; border-radius: 12px; padding: 20px; border: 1.5px solid #eee;'>
                <div style='font-size: 20px; font-weight: 700; color: #b00; margin-bottom: 16px; border-bottom: 2px solid #b00; padding-bottom: 8px;'>${sectionLabels[section]}</div>
          <table style='width: 100%; border-collapse: collapse; font-size: 16px; margin-top: 4px;'>
            <thead>
                    <tr style='background: #b00;'>
                      <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Task</th>
                      <th style='padding: 10px 8px; color: #fff; font-size: 15px; font-weight: 700; border-bottom: 2px solid #222;'>Completed</th>
              </tr>
            </thead>
            <tbody>
                    ${fieldLabels[section].map((field, i) => `
                      <tr style='background: ${i%2===0 ? '#fff' : '#f6f6f6'};'>
                        <td style='padding: 10px 8px; color: #222; border-bottom: 1px solid #eee; font-weight: 500;'>${field}</td>
                        <td style='text-align: center; padding: 10px 8px; border-bottom: 1px solid #eee; font-size: 20px; font-weight: 700; color: ${form[section][i] ? '#b00' : '#111'};'>${form[section][i] ? '<span style="font-size:22px; color:#b00;">✔️</span>' : '<span style="font-size:22px; color:#111;">✖️</span>'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
                <div style='margin-top: 24px;'>
                  <div style='font-size: 15px; font-weight: 600; color: #b00; margin-bottom: 6px;'>Comments:</div>
                  <div style='min-height: 48px; border: 1.5px solid #b00; border-radius: 8px; background: #fafbfc; padding: 10px 12px; font-size: 15px; color: #222;'>${comments[section] || '<span style="color:#bbb;">(No comments)</span>'}</div>
                </div>
              </div>
              `).join('')}
            <img src='${LOGO_URL}' alt='Logo' style='width: 90px; height: auto; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.08); display:block; margin: 32px auto 0 auto;' />
        </div>
      </div>
    `;

      // Generate PDF as blob
      const pdfBlob = await html2pdf()
      .set({
        margin: 0,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      })
        .from(pdfHtml)
        .outputPdf('blob');

      // Create filename with form name + date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Surgery_Audit_Form_${currentDate}.pdf`;

      // Save to Firebase
      const formData = {
        formType: 'surgery',
        title: 'Surgery Audit Form',
        location: 'Not specified',
        createdBy: auditor || 'Not specified',
        date: date || currentDate,
        staffName: auditor || 'Not specified',
        formData: form,
        comments: comments
      };

                   await savePDFForm(formData, pdfBlob, fileName);
      
      // Also download locally for user convenience
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('PDF saved to library and downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const mobileStyle = `
    @media (max-width: 600px) {
      .surgery-container { padding: 0 !important; border-radius: 0 !important; box-shadow: none !important; background: #f6f7fb !important; }
      .surgery-title { font-size: 1.3rem !important; }
      .surgery-checklistItem { padding: 10px 4px !important; border-radius: 8px !important; }
      .surgery-label { font-size: 13px !important; }
      .surgery-input, .surgery-textarea { font-size: 15px !important; padding: 8px !important; border-radius: 8px !important; width: 100% !important; min-width: 0 !important; max-width: 100vw !important; }
      .surgery-button { font-size: 16px !important; padding: 12px 0 !important; border-radius: 10px !important; max-width: 100vw !important; }
    }
  `;

  return (
    <div style={styles.container} className="surgery-container">
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 32, marginBottom: 18}}>
        <img src="/logo.jpg" alt="Married to the Mop Logo" style={{width: 90, height: 'auto', borderRadius: 12, boxShadow: '0 1px 4px rgba(176,0,0,0.08)'}} />
      </div>
      <style>{mobileStyle}</style>
      <div style={styles.title} className="surgery-title">Platt Bridge Surgery Cleaning Audit Form</div>
      <div style={styles.subtitle}>Please complete the checklist below. All items require a Yes/No answer. Add comments at the end if needed.</div>
      <div style={{ marginBottom: 18 }}>
        <div style={styles.label}>Date:</div>
        <input
          style={styles.input}
          className="surgery-input"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
        />
      </div>
      <div style={{ marginBottom: 18 }}>
        <div style={styles.label}>Auditor Name:</div>
        <input
          style={styles.input}
          className="surgery-input"
          type="text"
          value={auditor}
          onChange={e => setAuditor(e.target.value)}
          placeholder="Enter auditor name..."
        />
      </div>
      <div style={{ marginBottom: 18 }}>
        <div style={styles.label}>Cleaner(s):</div>
        <input
          style={styles.input}
          className="surgery-input"
          type="text"
          value={cleaners}
          onChange={e => setCleaners(e.target.value)}
          placeholder="Enter cleaner(s) name..."
        />
      </div>
      <div style={styles.section}>
        <div style={styles.label}>General Areas</div>
        {GENERAL_AREAS.map((item, idx) => (
          <div key={idx} style={styles.checklistItem} className="surgery-checklistItem">
            <span>{item}</span>
            <div style={styles.radioGroup}>
              <label>
                <input
                  type="radio"
                  name={`general-${idx}`}
                  value="yes"
                  checked={form.general[idx] === 'yes'}
                  onChange={() => handleGeneralChange(idx, 'yes')}
                />{' '}Yes
              </label>
              <label>
                <input
                  type="radio"
                  name={`general-${idx}`}
                  value="no"
                  checked={form.general[idx] === 'no'}
                  onChange={() => handleGeneralChange(idx, 'no')}
                />{' '}No
              </label>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 16 }}>
          <div style={styles.label}>Comments:</div>
          <textarea
            style={styles.textarea}
            className="surgery-textarea"
            value={comments.general}
            onChange={e => handleCommentChange('general', e.target.value)}
            placeholder="Add comments for General Areas..."
          />
        </div>
      </div>
      <div style={styles.section}>
        <div style={styles.label}>Reception Area</div>
        {RECEPTION_AREA.map((item, idx) => (
          <div key={idx} style={styles.checklistItem} className="surgery-checklistItem">
            <span>{item}</span>
            <div style={styles.radioGroup}>
              <label>
                <input
                  type="radio"
                  name={`reception-${idx}`}
                  value="yes"
                  checked={form.reception[idx] === 'yes'}
                  onChange={() => handleReceptionChange(idx, 'yes')}
                />{' '}Yes
              </label>
              <label>
                <input
                  type="radio"
                  name={`reception-${idx}`}
                  value="no"
                  checked={form.reception[idx] === 'no'}
                  onChange={() => handleReceptionChange(idx, 'no')}
                />{' '}No
              </label>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 16 }}>
          <div style={styles.label}>Comments:</div>
          <textarea
            style={styles.textarea}
            className="surgery-textarea"
            value={comments.reception}
            onChange={e => handleCommentChange('reception', e.target.value)}
            placeholder="Add comments for Reception Area..."
          />
        </div>
      </div>
      <div style={styles.section}>
        <div style={styles.label}>Clinical Rooms</div>
        {CLINICAL_ROOMS.map((item, idx) => (
          <div key={idx} style={styles.checklistItem} className="surgery-checklistItem">
            <span>{item}</span>
            <div style={styles.radioGroup}>
              <label>
                <input
                  type="radio"
                  name={`clinical-${idx}`}
                  value="yes"
                  checked={form.clinical[idx] === 'yes'}
                  onChange={() => handleClinicalChange(idx, 'yes')}
                />{' '}Yes
              </label>
              <label>
                <input
                  type="radio"
                  name={`clinical-${idx}`}
                  value="no"
                  checked={form.clinical[idx] === 'no'}
                  onChange={() => handleClinicalChange(idx, 'no')}
                />{' '}No
              </label>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 16 }}>
          <div style={styles.label}>Comments:</div>
          <textarea
            style={styles.textarea}
            className="surgery-textarea"
            value={comments.clinical}
            onChange={e => handleCommentChange('clinical', e.target.value)}
            placeholder="Add comments for Clinical Rooms..."
          />
        </div>
      </div>
      <div style={styles.section}>
        <div style={styles.label}>Offices</div>
        {OFFICES.map((item, idx) => (
          <div key={idx} style={styles.checklistItem} className="surgery-checklistItem">
            <span>{item}</span>
            <div style={styles.radioGroup}>
              <label>
                <input
                  type="radio"
                  name={`offices-${idx}`}
                  value="yes"
                  checked={form.offices[idx] === 'yes'}
                  onChange={() => handleOfficesChange(idx, 'yes')}
                />{' '}Yes
              </label>
              <label>
                <input
                  type="radio"
                  name={`offices-${idx}`}
                  value="no"
                  checked={form.offices[idx] === 'no'}
                  onChange={() => handleOfficesChange(idx, 'no')}
                />{' '}No
              </label>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 16 }}>
          <div style={styles.label}>Comments:</div>
          <textarea
            style={styles.textarea}
            className="surgery-textarea"
            value={comments.offices}
            onChange={e => handleCommentChange('offices', e.target.value)}
            placeholder="Add comments for Offices..."
          />
        </div>
      </div>
      <div style={styles.section}>
        <div style={styles.label}>Kitchenette</div>
        {KITCHENETTE.map((item, idx) => (
          <div key={idx} style={styles.checklistItem} className="surgery-checklistItem">
            <span>{item}</span>
            <div style={styles.radioGroup}>
              <label>
                <input
                  type="radio"
                  name={`kitchenette-${idx}`}
                  value="yes"
                  checked={form.kitchenette[idx] === 'yes'}
                  onChange={() => handleKitchenetteChange(idx, 'yes')}
                />{' '}Yes
              </label>
              <label>
                <input
                  type="radio"
                  name={`kitchenette-${idx}`}
                  value="no"
                  checked={form.kitchenette[idx] === 'no'}
                  onChange={() => handleKitchenetteChange(idx, 'no')}
                />{' '}No
              </label>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 16 }}>
        <div style={styles.label}>Comments:</div>
        <textarea
          style={styles.textarea}
          className="surgery-textarea"
            value={comments.kitchenette}
            onChange={e => handleCommentChange('kitchenette', e.target.value)}
            placeholder="Add comments for Kitchenette..."
          />
        </div>
      </div>
      <button style={styles.button} className="surgery-button" onClick={generatePDF}>Download PDF</button>
      <img src="/logo.jpg" alt="Married to the Mop Logo" style={styles.logo} />
    </div>
  );
}
